import { Injectable } from '@angular/core';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { RememberService } from '@app/core/services/remember.service';
import { Base64UrlService } from '@app/services/base64url.service';
import { GridMessage } from '@app/core/components/grid/grid.service';

@Injectable({
    providedIn: 'root',
})
export class OrderlineCalcFormService {
    constructor(
        private datatask: DataTaskService,
        private remember: RememberService,
        private base64: Base64UrlService,
    ) {}

    /**
     * Retrieves the calc form for the given params.
     *
     * @datatask 3872
     * @datataskName sp_ws_p2_w_orderlinecalcform_get
     * @param params for retrieving the calc form.
     * @returns promise containing the calc form.
     */
    public async getOrderlineCalcForm(params: OrderlineCalcFormParams): Promise<OrderlineCalcFormGet> {
        return (await this.datatask.Post(3872, params))[0];
    }

    /**
     * Sends in updated orderline calc and retrieves changes based on changed propterties.
     *
     * @datatask 3882
     * @datataskName sp_ws_p2_w_orderlinecalc_colchanged
     * @param params orderline calc form with colname_changed for changed property.
     * @returns a promise containing new values to update the orderline calc form.
     */
    public async changesOrderlineCalcForm(params: UpdateOrderlineCalcFormParams): Promise<Partial<OrderlineCalcFormGet>> {
        return (await this.datatask.Post(3882, params))[0];
    }

    /**
     * Approves the given orderline calc form.
     *
     * @datatask 3891
     * @datataskName sp_ws_p2_w_orderlinecalc_approve
     * @param params the get for orderline calc form to save.
     * @returns promise containing errorcode and message.
     */
    public async saveOrderlineCalcForm(params: OrderlineCalcFormGet): Promise<SaveOrderlineCalcFormResponse> {
        const response = (await this.datatask.Post(3891, params))[0];
        let messages: GridMessage[] = [];

        try {
            messages = JSON.parse(response.messages ?? '[]');
        } catch (error) {
            console.error(error);
        }

        return { ...response, messages };
    }

    /**
     * Persist updated the given orderline calc form.
     *
     * @datatask 3901
     * @datataskName sp_ws_p2_w_orderlinecalc_update
     * @param params the get for orderline calc form to save.
     * @returns promise containing errorcode and message.
     */
    public async updateOrderlineCalcForm(params: OrderlineCalcFormGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3901, params))[0];
    }

    /**
     * Persists the given state of the orderline calc form.
     *
     * @param params orderline calc form to persists.
     * @returns when remember is completed.
     */
    public async rememberOrderlineCalcForm(params: OrderlineCalcFormGet) {
        return this.remember.remember(this.getOrderlineCalcFormRememberId(params.order_internal_no), this.base64.urlEncode(JSON.stringify(params)));
    }

    /**
     * Resets the remember for the given orderline calc form.
     *
     * @param params the orderline calc form to reset.
     * @returns orderline calc form has been resetted.
     */
    public async resetOrderlineCalcForm(params: OrderlineCalcFormGet): Promise<[void, ErrorMessageResponse]> {
        return await Promise.all([this.remember.remember(this.getOrderlineCalcFormRememberId(params.order_internal_no), ''), (await this.datatask.Post(3879, params))[0]]);
    }

    /**
     * Searches for material grades.
     *
     * @datatask 3874
     * @datataskName sp_ws_p2_materialgrade_typeahead_list
     * @param params query for list of material grades.
     * @return promise containing list of material grades matching the params.
     */
    public searchMaterialGrades(params: SearchMaterialGradesParams): Promise<MaterialGradeItem[]> {
        return this.datatask.Post(3874, params);
    }

    /**
     * Searches for certification requirements.
     *
     * @datatask 3875
     * @datataskName sp_ws_p2_certificaterequirement_typeahead_list
     * @param params query for list of certification requirements.
     * @return promise containing list of certification requirements matching the params.
     */
    public searchCertificateRequirements(params: SearchCertificateReuirementsParams): Promise<CertificateRequirementItem[]> {
        return this.datatask.Post(3875, params);
    }

    /**
     * Searches for ultrassonic classes.
     *
     * @datatask 3876
     * @datataskName sp_ws_p2_ultrasonicclass_typeahead_list
     * @param params query for list of ultrassonic classes.
     * @return promise containing list of ultrassonic classes matching the params.
     */
    public searchUltraSonicClasses(params: SearchUltraSonicClassesParams): Promise<UltraSonicClassItem[]> {
        return this.datatask.Post(3876, params);
    }

    /**
     * Gets the page load for calc copy from component.
     *
     * @datatask 3895
     * @datataskName sp_ws_p2_w_calccopyfrom_get
     * @param params calc keyno for the calc to copy to.
     * @returns page load for the calc copy.
     */
    public async getCalcCopyFrom(params: CalcCopyFromParams): Promise<CalcCopyFromGet> {
        return (await this.datatask.Post(3895, params))[0];
    }

    /**
     * Copies the calc.
     *
     * @datatask 3897
     * @datataskName ssp_ws_p2_w_calccopyfrom_copy
     * @param params the calc copy get with keyno to copy from..
     * @returns promise containing error response.
     */
    public async saveCalcCopyFrom(params: CalcCopyFromGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3897, params))[0];
    }

    private getOrderlineCalcFormRememberId(order_internal_no: number | string): string {
        return `w_orderlinecalcform_remember.${order_internal_no}`;
    }
}

export interface OrderlineCalcFormParams {
    order_internal_no: string;
    argtype: string;
    argvalue: string;
}

export interface OrderlineCalcFormGet {
    order_internal_no: string;
    argtype: string;
    argvalue: string;
    calc_keyno: string;
    orderline_keyno: string;
    webpage_heading: string;
    prod_name: string;
    ordered_quantity: string | number;
    w_orderlinecalcform_remember: string;
    dimension: string;
    weight_net_kg: string | number;
    weight_workpiece_kg: string | number;
    materialgrade_name: string;
    materialgrade_keyno: string;
    certificaterequirement_keyno: string;
    certificaterequirement_name: string;
    ultrasonicclass_keyno: string;
    ultrasonicclass_name: string;
    productionrouting_keyno: string;
    productionrouting_name: string;
    prodgrp_id: string;
    prodgrp_name: string;
    options_selected: string;
    cost_amount_locval_pr_sku: string | number;
    markup_pst: string | number;
    sales_price_locval_pr_sku: string | number;
    sales_price_locval_pr_kg: string | number;
    amount_locval_total: string | number;
    margin_locval_total: string | number;
    workpiece_thickness_mm: string | number;
    is_saved: '1' | '0';
}

export interface SaveOrderlineCalcFormResponse extends ErrorMessageResponse {
    messages: GridMessage[];
}

export interface MaterialGradeItem {
    materialgrade_keyno: string;
    materialgrade_name: string;
    density_kg_pr_dm3: string;
    prod_id: string;
}

export interface CertificateRequirementItem {
    certificaterequirement_keyno: string;
    certificaterequirement_name: string;
}

export interface UltraSonicClassItem {
    ultrasonicclass_keyno: string;
    ultrasonicclass_name: string;
}

export interface SearchUltraSonicClassesParams {
    searchtext: string;
}

export interface SearchCertificateReuirementsParams {
    searchtext: string;
}

export interface SearchMaterialGradesParams {
    searchtext: string;
}

export interface UpdateOrderlineCalcFormParams extends OrderlineCalcFormGet {
    colname_changed: keyof OrderlineCalcFormGet;
}

export interface CalcCopyFromParams {
    calc_keyno: string;
}

export interface CalcCopyFromGet {
    calc_keyno_copy_to: string;
    cust_no: string;
    cust_name: string;
    calc_keyno_copy_from: string;
    order_internal_no: string;
}
